import React, { useState, useEffect } from "react";
import { backend, API_URL } from "../../../config";
import {
  Button,
  Typography,
  Modal,
  CircularProgress,
  Container,
  Grid,
  TextField,
  InputLabel,
  Autocomplete,
} from "@mui/material";

import JSZip from "jszip";
import writeXlsxFile from "write-excel-file";

import ManagementNav from "../../../features/OrderManagement/components/Nav/ManagementNav";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
}));

export const ExportOrderlists = (props) => {
  const classes = useStyles();

  const [confirmModal, setConfirmModal] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [customersSelected, setCustomersSelected] = useState([]);

  useEffect(() => {
    const getCustomers = async () => {
      await backend
        .get(`${API_URL}companylocations?no_page=1`)
        .then(({ data }) => {
          if (data && data.length) {
            const sorted = data.sort((a, b) => 
              (a.name || "").localeCompare(b.name || "")
            );
            setCustomers(sorted);
            setCustomersSelected(sorted);
          }
        });
    };

    getCustomers();
  }, []);

  const handleChangeCustomers = (e, value) => {
    setCustomersSelected(value);
  };

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Are you sure you want to bulk export orderlists?
          <div align="right">
            <Button
              onClick={() => toggleConfirmModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
              className={classes.button}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Exporting Orderlists <br />
              This may take some time relative to the number of customers selected
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while exporting orderlists
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "scroll",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <b>Error:</b>
                <br />
                <div dangerouslySetInnerHTML={{ __html: errorInfo.message }} />
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveRequest()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The orderlists were exported successfully.
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const resolveRequest = () => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    toggleConfirmModal();
    setErrorInfo({ error: false, message: "" });
  };

  const sendRequest = async () => {
    setLoadingFlag(true);

    const zipFile = new JSZip();
    let hasExportedAtLeastOne = false;

    Promise.all(
      customersSelected.map(async (customer) => {
        return await backend
          .get(`${API_URL}orderlists?no_page=1&company_id=${customer.company_id}`)
          .then(async ({ data }) => {
              if (data && data.length > 0) {
                const orderListData = data[0];

                if (orderListData.categories && orderListData.categories.length > 0) {
                 let orderListDataExport = [];

                 orderListData.categories.forEach((category) => {
                   orderListDataExport.push([
                     { value: category.category_name, type: String },
                   ]);

                   category.rows.forEach((row) => {
                     orderListDataExport.push([
                       { value: "" + row.product_id, type: String },
                     ]);
                   });

                    orderListDataExport.push([{ value: "", type: String }]);
                  });

                  const blob = await writeXlsxFile(orderListDataExport);
                  
                 const safeCustomerName = (customer.name || `Customer_${customer.id}`).replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_");
                 zipFile.file(`${safeCustomerName}_Order_Guide_export.xlsx`, blob);
                 hasExportedAtLeastOne = true;
               }
             }
          })
          .catch((error) => {
            console.log(error);
            const errorData = error.response ? error.response.data : error;

            setErrorInfo((prevState) => {
              let newMsg =
                typeof errorData === "object"
                  ? errorData.hasOwnProperty("message")
                    ? errorData.message
                    : errorData.detail || error.message
                  : error.message;

              if (newMsg !== "" && newMsg !== undefined) {
                const errorMsg =
                  prevState.message +
                  `${prevState.message === "" ? "" : "<br />"}` +
                  newMsg;

                return {
                  error: true,
                  message: errorMsg,
                };
              }
              return prevState;
            });
          });
      })
    ).then(async () => {
      if (hasExportedAtLeastOne && !errorInfo.error) {
        await zipFile.generateAsync({ type: "blob" }).then((content) => {
          const blob = new Blob([content], { type: "application/zip" });
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");

          const cDateParam = new Date().toISOString().split("T")[0];

          link.href = blobUrl;
          link.setAttribute("download", `${cDateParam}_orderlists_export.zip`);
          link.click();
          link.remove();

          setLoadingFlag(false);
          setFinishedFlag(true);
        });
      } else if (!hasExportedAtLeastOne && !errorInfo.error) {
         setErrorInfo({ error: true, message: "No categories found for any of the selected customers." });
         setLoadingFlag(false);
      } else {
         setLoadingFlag(false);
      }
    });
  };

  return (
    <Container style={{ minHeight: "80vh" }}>
      <Modal open={confirmModal} onClose={toggleConfirmModal}>
        {confirmModalContent}
      </Modal>
      <ManagementNav {...props} />
      <Typography component="h6" variant="h6" align="center">
        Export Orderlist
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Use this interface to export orderlists (Order Guides) of selected customers in ZIP format.
        </Grid>
        <Grid item xs={12} sm={12}></Grid>
        
        <Grid item xs={12} sm={12}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "0.5rem" }}>
            <InputLabel shrink id="customers" style={{ margin: 0 }}>
              Customers
            </InputLabel>
            <div>
              <Button size="small" onClick={() => setCustomersSelected(customers)} style={{ padding: "0 8px", minWidth: 0, textTransform: "none" }}>Select all</Button>
              <Button size="small" onClick={() => setCustomersSelected([])} style={{ padding: "0 8px", minWidth: 0, textTransform: "none" }}>Deselect all</Button>
            </div>
          </div>
          <Autocomplete
            fullWidth
            multiple
            disablePortal
            limitTags={4}
            size="small"
            name="customers"
            options={customers}
            getOptionLabel={(option) => option.name || `Customer #${option.id}`}
            renderInput={(params) => (
              <TextField {...params} size="small" variant="standard" />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(e, value) => handleChangeCustomers(e, value)}
            value={customersSelected}
          />
        </Grid>
        
        <Grid item xs={12} sm={12} style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => toggleConfirmModal()}
            className={classes.button}
            disabled={!customersSelected.length || loadingFlag}
          >
            Export customers
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ExportOrderlists;
